import { Link } from 'react-router-dom';

export type ArticleItem = {
  id: string;
  title: string;
  categoryName: string;
  author: string;
  date: string;
  readTime: string;
  heat: number;
  summary: string;
  image: string;
  tags: string[];
};

type ArticleCardProps = {
  article: ArticleItem;
  variant?: 'default' | 'horizontal' | 'compact';
  index?: number;
};

export default function ArticleCard({ article, variant = 'default', index }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-4 border-b border-background-200 py-4 last:border-b-0"
      >
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 font-heading text-sm font-bold text-primary-700">
          {typeof index === 'number' ? String(index + 1).padStart(2, '0') : ''}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
            <span>{article.categoryName}</span>
            <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
            <span>{article.date}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group grid grid-cols-1 gap-5 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:grid-cols-[240px_1fr] md:p-5"
      >
        <div className="h-44 w-full overflow-hidden rounded-md sm:h-[150px]">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 看料大瓜`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-col justify-center">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-secondary-900">{article.categoryName}</span>
            <span className="text-foreground-500">{article.date}</span>
            <span className="text-foreground-400">· {article.readTime}</span>
          </div>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
          <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-500" aria-hidden="true"></i>
              热度 {article.heat}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="h-52 w-full overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} - 看料大瓜`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-accent-100 px-2.5 py-1 text-accent-900">{article.categoryName}</span>
          <span className="text-foreground-500">{article.readTime}</span>
        </div>
        <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-1 text-primary-600">
            阅读话题
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </span>
        </div>
      </div>
    </Link>
  );
}